import {
	CompareTable,
	InShort,
	Prose,
	RelatedPages,
} from "@marketing/components/answer";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is an ecommerce AI agent?",
		a: "An ecommerce AI agent is software that shops a store's catalog for a customer. It reads a request in plain language, searches the live catalog, checks the results, adds products to the basket and prepares the checkout. A chatbot only answers; an agent acts, within rules the store sets.",
	},
	{
		q: "What is the difference between an AI agent and a chatbot for ecommerce?",
		a: "A chatbot answers questions, usually from help-centre content. An ecommerce agent uses tools: it searches the catalog with real prices and stock, changes the basket and builds a checkout. Hephon Commerce is an agent, and it still never places an order without the shopper's confirmation.",
	},
	{
		q: "Can an AI agent place orders on its own?",
		a: "Not with Hephon Commerce. It fills the basket and builds the checkout, but the order is placed only after the shopper confirms: a tap, a signed checkout page, or the keypad after the basket is read back on a call. A typed or spoken yes alone never places an order.",
	},
	{
		q: "Does it replace our site search?",
		a: "No. Your search bar and category pages keep their search engine. The agent answers requests people make in sentences, such as a budget, a recipe or a list, in chat, on WhatsApp and on calls.",
	},
	{
		q: "Does it replace our customer service tools?",
		a: "No. It is built for shopping, not for returns, refunds or delivery complaints. Every conversation is in a shared inbox where a person can take over, and handover to your help desk is possible by integration.",
	},
	{
		q: "Which platforms does it work with?",
		a: "Shopify, WooCommerce, product feeds, our REST API with signed webhooks, and custom connectors for retailers with their own platforms. Each branch can keep its own prices and stock.",
	},
	{
		q: "What is agentic shopping?",
		a: "Agentic shopping means an AI agent does the work of finding and choosing products and filling the basket, while the shopper approves the result. In grocery that means lists, recipes, budgets and repeat orders handled in a sentence.",
	},
	{
		q: "How is Hephon Commerce priced?",
		a: "Pricing is a proposal per retailer and depends on your stores and the channels you use. Book a demo and we will send one.",
	},
];

const KINDS = [
	[
		"What it does",
		"Answers questions",
		"Ranks results for a typed query",
		"Searches, checks, adds to the basket, builds the checkout",
	],
	[
		"What it reads",
		"Help-centre articles, scripted flows",
		"The catalog index",
		"The live catalog, the shopper's branch, the basket",
	],
	[
		"Input it handles best",
		"Common questions",
		"Short keywords",
		"Sentences: lists, budgets, recipes, follow-ups",
	],
	[
		"Changes the basket",
		"Rarely",
		"No",
		"Yes, and only orders after the shopper confirms",
	],
];

const COMPARE_COLUMNS = [
	"",
	"Support agents (e.g. Gorgias, Zendesk AI, Intercom Fin)",
	"Shopping agents (e.g. Hephon Commerce)",
	"Search engines (e.g. Constructor, Algolia)",
];

const COMPARE = [
	[
		"Main job",
		"Resolve customer service questions and tickets",
		"Turn a shopper's request into a basket",
		"Rank products in the search bar and category pages",
	],
	[
		"Typical questions",
		"Where is my order? Can I return this?",
		"Kabsa for 6. Cheapest milk. Snacks under 50 riyals.",
		"A typed keyword: milk, rice, shampoo",
	],
	[
		"Works from",
		"Help centre, policies, order data, tickets",
		"Live catalog, branch prices and stock, basket",
		"Catalog index, behaviour data, merchandising rules",
	],
	[
		"Acts on the basket",
		"Usually not",
		"Yes: adds, changes quantities, builds checkout",
		"No, the shopper clicks",
	],
	[
		"Where it lives",
		"Help widget, email, social inboxes",
		"Your app, website, WhatsApp, phone calls",
		"Your site and app search",
	],
	[
		"Where it is stronger",
		"Ticketing, agent workflows, support analytics",
		"Multi-item requests, recipes, budgets, dialects, voice",
		"Speed at scale, merchandising control, search analytics",
	],
	[
		"Use it alongside the others?",
		"Yes, for after the order",
		"Yes, for shopping in conversation",
		"Yes, for the search bar",
	],
];

const FIRST = [
	{
		title: "Search in shoppers' words",
		text: 'Requests as people write them: Arabic, Arabizi, English, misspelled, mixed. "7aleeb" finds milk; "tomato" finds Tomatoes.',
	},
	{
		title: "Lists in one message",
		text: '"Milk, bread and eggs" returns a row per item, so the shopper picks each without asking three times.',
	},
	{
		title: "Recipes to baskets",
		text: "A dish and a number of people becomes ingredient lines, the right packs and one tap to add them all.",
	},
	{
		title: "Budgets and cheapest",
		text: '"Under 50 riyals" and "cheapest first" are understood and held for follow-ups.',
	},
	{
		title: "Orders on WhatsApp",
		text: "Product cards, a basket and a checkout the shopper confirms with a tap on your own number.",
	},
	{
		title: "Orders by phone",
		text: "A caller orders by voice; the basket is read back and the keypad confirms.",
	},
];

const LIVE = [
	{
		title: "Connect your catalog",
		text: "Shopify, WooCommerce, a product feed or your API, with each branch's prices and stock.",
	},
	{
		title: "Set the rules and test",
		text: "Persona, languages, products you never sell, handover. Then a test suite built from your own catalog.",
	},
	{
		title: "Go live channel by channel",
		text: "In your app first, or WhatsApp first; add phone orders when you are ready.",
	},
];

export const Route = createFileRoute("/_marketing/ai-agent-for-ecommerce")({
	head: () =>
		marketingHead({
			title: "Ecommerce AI Agent for Grocery and Retail | Hephon Commerce",
			description:
				"An ecommerce AI agent that searches your live catalog, fills the basket and orders only after the shopper confirms. For grocery, in app and on WhatsApp.",
			path: "/ai-agent-for-ecommerce",
			breadcrumb: ["Ecommerce AI agent"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: AiAgentForEcommercePage,
});

function AiAgentForEcommercePage() {
	return (
		<>
			<PageIntro
				kicker="Ecommerce AI agent"
				title="An ecommerce AI agent that shops your catalog and fills the basket"
				lead="It reads what a shopper wants, searches your live catalog, checks the results and builds the basket. The shopper confirms before anything is ordered."
			/>

			<InShort updated={UPDATED}>
				<p>
					An ecommerce AI agent is software that shops a store's
					catalog for a customer: it searches, checks what it found,
					adds to the basket and prepares the checkout. Hephon
					Commerce is an ecommerce agent for supermarkets, grocery
					delivery, quick commerce and other retailers with a product
					catalog.
				</p>
				<p>
					It works inside the retailer's own apps and website, on its
					WhatsApp Business number and on phone calls, in Arabic,
					Arabizi, English and French, and it never places an order
					without the shopper's confirmation.
				</p>
			</InShort>

			<Section id="what-title" title="What is an ecommerce AI agent?">
				<Prose>
					<p>
						An ecommerce agent is the difference between being told
						where the milk is and having it put in your basket.
						Three kinds of software get called "AI for ecommerce",
						and they do different jobs.
					</p>
					<p>
						A <strong>chatbot</strong> answers questions. It is
						usually trained on help-centre pages and scripted flows:
						opening hours, delivery fees, return policy. It talks;
						it does not shop.
					</p>
					<p>
						<strong>Site search</strong> ranks products for what a
						shopper types in the search bar. It is fast and
						essential, and it is built for short keywords. "Snacks
						for six people watching the game under 20 dollars" is
						not a keyword.
					</p>
					<p>
						An <strong>ecommerce AI agent</strong> uses tools. It
						searches the live catalog with the shopper's branch
						prices and stock, checks that what it found matches the
						request, adds products to the basket, changes quantities
						and builds a checkout. This is what people mean by
						agentic shopping: the agent does the legwork, and the
						shopper approves the result.
					</p>
				</Prose>
				<div className="mx-auto mt-12 max-w-4xl">
					<CompareTable
						columns={[
							"",
							"Chatbot",
							"Site search",
							"Ecommerce AI agent",
						]}
						rows={KINDS}
						caption="Three kinds of AI for ecommerce, by what they do."
					/>
				</div>
			</Section>

			<Section
				id="first-title"
				tone="mist"
				title="What should an ecommerce agent automate first?"
				lead="In grocery, start with the requests that are slow for shoppers and that a search bar handles badly."
			>
				<DetailGrid items={FIRST} />
				<Prose className="mt-14">
					<p>
						Grocery baskets are long, repeat often and depend on
						details a search bar ignores: pack sizes, the branch
						that will deliver, budgets, and what a family is cooking
						tonight. Those are the requests where an agent saves the
						most time, because each one would otherwise take several
						searches and taps.
					</p>
					<p>
						Leave to people what needs judgement or authority:
						complaints, a late or wrong delivery, refunds, a large
						catering order, anything to do with an account or a
						payment dispute. The assistant can recognise these and
						hand the conversation to your team, who see it in the
						same inbox with everything said so far. A shopper can
						ask for a person at any time.
					</p>
				</Prose>
			</Section>

			<Section id="safe-title" title="How does it act safely?">
				<Prose>
					<p>
						An agent that can change a basket needs harder limits
						than a chatbot that only talks. Hephon Commerce puts
						them in the server, not only in the instructions to the
						model, so no wording from a shopper can talk its way
						past them.
					</p>
					<h3>The shopper confirms every order</h3>
					<p>
						No tool the model can call places an order. The agent
						fills the basket, in sync with your app's cart, and
						builds a checkout. The order is placed only by the
						shopper's explicit confirmation: a tap on WhatsApp, a
						signed checkout page, or pressing 1 on the keypad after
						the basket is read back on a call. A typed or spoken
						"yes" alone never places an order. The order then goes
						to your platform through its API, or to your team as an
						order ticket.
					</p>
					<h3>Your rules are enforced on the server</h3>
					<p>
						A retailer can hide alcohol, pork and tobacco, or all
						three for a halal store. Those products are removed by
						the server before the agent sees the catalog, on every
						channel. Each product is judged by an AI model, not a
						word list, and a product that has not been checked yet
						stays hidden. On WhatsApp, alcohol and tobacco are
						always excluded. Out-of-stock products cannot be added,
						and prices and stock come from the shopper's own branch.
					</p>
					<h3>It is tested on your catalog first</h3>
					<p>
						Before launch, and before any change to the prompt, the
						model or search goes live, a test suite generated from
						your own catalog runs: every department covered,
						retrieval of real products, basket changes, the language
						of each reply, safety, and WhatsApp flows. Generic
						benchmarks say little about whether an agent finds your
						1 L Almarai milk when a shopper writes "7aleeb".
					</p>
					<h3>Off-topic results are dropped</h3>
					<p>
						Every search's top results are checked against the
						shopper's actual message before they are shown. A
						request for snacks for a basketball night shows snacks,
						not basketballs. The agent also never assumes food: a
						store that sells phones gets phones.
					</p>
				</Prose>
			</Section>

			<Section
				id="compare-title"
				tone="mist"
				title="Support agents, shopping agents and search engines compared"
				lead="Most retailers need more than one. This is what each kind is built for, stated generally; check each vendor's current features."
			>
				<div className="mx-auto max-w-5xl">
					<CompareTable
						columns={COMPARE_COLUMNS}
						rows={COMPARE}
						caption="Categories compared as of 5 November 2026. Named products are examples of each category; several now add AI features beyond their core job."
					/>
				</div>
				<Prose className="mt-14">
					<p>
						Support agents such as Gorgias, Zendesk AI and Intercom
						Fin are strong at what happens after the order: tracking
						questions, returns, policies, routing tickets to the
						right person, and reporting on support quality. If most
						of your chat volume is "where is my order", start there.
					</p>
					<p>
						Search engines such as Constructor and Algolia power the
						search bar and category pages at large scale, with
						merchandising controls and analytics a team can tune.
						They are the right tool for the millions of short
						queries a big store gets, and both offer conversational
						features of their own.
					</p>
					<p>
						A shopping agent sits in the conversation: it takes a
						sentence, a voice note or a call and turns it into a
						basket. Hephon Commerce is built for that, for grocery
						in particular, and for shoppers who write and speak
						Gulf, Saudi or Lebanese Arabic, Arabizi and English.
					</p>
				</Prose>
			</Section>

			<Section id="where-title" title="Where does the agent live?">
				<Prose>
					<p>
						Shoppers should not have to go somewhere new. The same
						agent, with one basket, works in three places:
					</p>
					<ul>
						<li>
							<strong>Your app and website.</strong> An SDK for
							iOS, Android and the web, with React Native and
							Flutter, plus a hosted chat page per retailer. The
							basket stays in sync with your app's cart.
						</li>
						<li>
							<strong>Your WhatsApp Business number.</strong>{" "}
							Product cards, a basket, a checkout confirmed with a
							tap, voice notes and WhatsApp calls.
						</li>
						<li>
							<strong>Phone calls.</strong> Voice ordering in Gulf
							Arabic and more than 30 languages, with replies in
							under a second and a person on hand for any caller
							who wants one.
						</li>
					</ul>
				</Prose>
			</Section>

			<Section
				id="fit-title"
				tone="mist"
				title="When Hephon Commerce is not the right fit"
			>
				<Prose>
					<ul>
						<li>
							<strong>You have no online catalog.</strong> The
							agent works from your live products, prices and
							stock. Without them it has nothing to shop.
						</li>
						<li>
							<strong>You want to replace site search.</strong>{" "}
							Hephon Commerce does not power your search bar or
							category pages.
						</li>
						<li>
							<strong>You want self-serve sign-up.</strong> Access
							is by invitation, after a demo on your own catalog.
						</li>
					</ul>
				</Prose>
			</Section>

			<Section id="live-title" title="Live in three steps." center>
				<Steps items={LIVE} />
			</Section>

			<Faq items={FAQ} title="Ecommerce AI agents, answered." />
			<RelatedPages
				links={[
					{
						href: "/how-it-works",
						label: "How the shopping agent works",
					},
					{
						href: "/agentic-commerce",
						label: "Agentic commerce guide",
					},
					{ href: "/shoppable-recipes", label: "Shoppable recipes" },
					{ href: "/supermarkets", label: "AI for supermarkets" },
					{ href: "/integrations", label: "Integrations" },
					{ href: "/security", label: "Security" },
				]}
			/>
			<Closing />
		</>
	);
}
